/* eslint-disable */
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Spinner,
  ButtonGroup,
  Input,
} from "reactstrap";
import "./../assets/css/LiveTranscript.css";
import {
  fileShareUrl,
  getFileShareText,
  headFileShare,
  putFileShareJson,
} from "../services/fileshare";

// ---- utils p/ speakers ----
function parseSpeakersText(text) {
  const map = {};
  const parts = (text || "").split(",");
  for (const p of parts) {
    const [id, ...rest] = p.trim().split(":");
    if (id && rest.length) map[id.trim()] = rest.join(":").trim();
  }
  return map;
}

function labelToId(label) {
  const m = String(label || "")
    .trim()
    .match(/(\d+)/);
  return m ? m[1] : "";
}
function applySpeakerMapToText(text, map) {
  if (!text || !map || Object.keys(map).length === 0) return text;
  return text
    .split(/\r?\n/)
    .map((line) => {
      const mm = line.match(/^\s*([^:]+):\s*(.*)$/);
      if (!mm) return line;
      const rawLabel = mm[1].trim();
      const rest = mm[2] ?? "";
      const id = labelToId(rawLabel);
      const pretty = id && map[id] ? map[id] : null;
      return pretty ? `${pretty}: ${rest}` : line;
    })
    .join("\n");
}

const engineToFile = (engine) => {
  switch (engine) {
    case "formatted":
      return "formatted.txt";
    case "azure":
      // transcricao.txt agora é único — quem alimenta é o provider ativo e o
      // mode ativo (config/active_provider + config/active_mode no fileshare).
      return "transcricao.txt";
    case "assembly":
      // contingência: lê direto a auditoria do principal (rota manual)
      return null;
    case "deepgram":
      return null;
    default:
      return null;
  }
};

const LiveTranscriptPage2 = ({ fileNameCurrent }) => {
  const [engine, setEngine] = useState("azure"); // 'assembly' | 'azure' | 'deepgram' | 'formatted'
  const [displayText, setDisplayText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [speakersText, setSpeakersText] = useState("");
  const [speakersMap, setSpeakersMap] = useState({});

  const fullTextRef = useRef("");
  const fetchIntervalRef = useRef(null);
  const textEndRef = useRef(null);

  const transcriptPath = useMemo(() => {
    const f = engineToFile(engine);
    if (!f || !fileNameCurrent) return null;
    return `${fileNameCurrent}/${f}`;
  }, [engine, fileNameCurrent]);

  const speakersPath = useMemo(
    () => (fileNameCurrent ? `${fileNameCurrent}/speakers.json` : null),
    [fileNameCurrent]
  );

  const fetchTranscript = useCallback(async () => {
    if (!transcriptPath) return;
    try {
      const text = await getFileShareText(transcriptPath);
      try {
        const meta = await headFileShare(transcriptPath);
        if (meta.lastModified) setLastUpdated(new Date(meta.lastModified));
      } catch {
        /* HEAD não-bloqueante */
      }

      setLoading(false);
      setError(null);

      const oldText = fullTextRef.current;
      if (text.length > oldText.length) {
        const newChunk = text.slice(oldText.length);
        fullTextRef.current = text;
        setDisplayText((prev) => prev + newChunk);
      } else if (text.length < oldText.length) {
        fullTextRef.current = text;
        setDisplayText(text);
      }
    } catch (err) {
      console.error("Erro ao buscar transcrição:", err);
      setError(null);
      setLoading(false);
    }
  }, [transcriptPath]);

  useEffect(() => {
    // reset state ao (re)montar — cobre troca de engine, já que fetchTranscript
    // depende de transcriptPath que depende de engine
    fullTextRef.current = "";
    setDisplayText("");
    setLoading(true);

    fetchTranscript();
    fetchIntervalRef.current = setInterval(fetchTranscript, 1500);
    return () => {
      clearInterval(fetchIntervalRef.current);
    };
  }, [fetchTranscript]);

  useEffect(() => {
    if (textEndRef.current) {
      textEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [displayText]);

  const handleDownload = () => {
    const blob = new Blob([fullTextRef.current], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileNameCurrent || "transcription"}-${engine}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ------------ speakers ------------
  const loadSpeakers = useCallback(async () => {
    if (!speakersPath) return;
    try {
      const text = await getFileShareText(speakersPath);
      const json = JSON.parse(text);
      setSpeakersMap(json || {});
      const oneLine = Object.entries(json || {})
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
      setSpeakersText(oneLine);
    } catch {
      setSpeakersText("");
      setSpeakersMap({});
    }
  }, [speakersPath]);

  const saveSpeakers = useCallback(async () => {
    if (!speakersPath) return;
    const map = parseSpeakersText(speakersText);
    if (Object.keys(map).length === 0) {
      alert("Use o formato: 1: Nome  2: Outro Nome");
      return;
    }
    setSpeakersMap(map);
    try {
      await putFileShareJson(speakersPath, map);
      await loadSpeakers();
    } catch (e) {
      console.error("❌ erro ao salvar speakers.json:", e);
      alert(
        "Erro ao salvar speakers.json. Verifique SAS (write) e CORS no File Service."
      );
    }
  }, [speakersText, speakersPath, loadSpeakers]);

  useEffect(() => {
    loadSpeakers();
  }, [loadSpeakers]);

  useEffect(() => {
    const id = setInterval(() => {
      loadSpeakers();
    }, 15000);
    return () => clearInterval(id);
  }, [loadSpeakers]);

  const displayWithNames = useMemo(
    () => applySpeakerMapToText(displayText, speakersMap),
    [displayText, speakersMap]
  );
  // ---------------------------------------------------------------------

  return (
    <Container fluid className="live-transcript-page">
      <Row className="justify-content-center mt-4 mb-4">
        <Col xs="12" md="12" lg="11">
          <Card className="live-transcript-card shadow-sm">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Transcrição ao Vivo</h5>

              <ButtonGroup>
                <Button
                  color={engine === "azure" ? "primary" : "secondary"}
                  onClick={() => setEngine("azure")}
                  active={engine === "azure"}
                >
                  transcription
                </Button>
                <Button
                  color={engine === "formatted" ? "primary" : "secondary"}
                  onClick={() => setEngine("formatted")}
                  active={engine === "formatted"}
                >
                  Formatted
                </Button>
                {/* <Button
                  color={engine === "assembly" ? "primary" : "secondary"}
                  onClick={() => setEngine("assembly")}
                  active={engine === "assembly"}
                >
                  AssemblyAI
                </Button>
                <Button
                  color={engine === "deepgram" ? "primary" : "secondary"}
                  onClick={() => setEngine("deepgram")}
                  active={engine === "deepgram"}
                >
                  Deepgram
                </Button> */}
              </ButtonGroup>
            </CardHeader>

            <div className="px-3 pt-2">
              <div className="d-flex align-items-center gap-2 flex-wrap ms-auto justify-content-end">
                <small className="text-muted mr-2">Speakers:</small>
                <Input
                  type="text"
                  value={speakersText}
                  onChange={(e) => setSpeakersText(e.target.value)}
                  placeholder="ex: 1: Nome 1, 2: Nome 2"
                  style={{ width: 360 }}
                />
                <Button
                  className="ml-2 mb-2"
                  color="primary"
                  onClick={saveSpeakers}
                >
                  Salvar
                </Button>
              </div>
            </div>

            <CardBody className="live-transcript-body">
              {loading && !displayText ? (
                <div className="live-transcript-loading">
                  <Spinner color="primary" />
                </div>
              ) : error ? (
                <div className="text-danger small">{error}</div>
              ) : (
                <pre className="typed-text">
                  {displayWithNames}
                  <span className="blinking-cursor">|</span>
                  <div ref={textEndRef} />
                </pre>
              )}
            </CardBody>

            <CardFooter className="d-flex justify-content-between align-items-center small text-muted">
              <span>
                {fullTextRef.current.length.toLocaleString()} caracteres
                {lastUpdated && (
                  <>
                    {" "}
                    &middot; Arquivo atualizado em:{" "}
                    {lastUpdated.toLocaleString()}
                  </>
                )}
              </span>
              <Button
                size="sm"
                color="success"
                onClick={handleDownload}
                disabled={loading || !displayText}
              >
                Baixar TXT
              </Button>
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LiveTranscriptPage2;
